import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, InputNumber, message, Select, Spin, Tooltip, Typography } from 'antd';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import { withRouteChange } from '../../services/APM';

interface VixtingAdmissionSettingsFormValues {
  vixtingDaysToAdmission: number;
  vixtingAdmissionDateReference: 'admission_date' | 'current_date';
}

const VixtingAdmissionSettings = () => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const [form] = Form.useForm<VixtingAdmissionSettingsFormValues>();
  const config = useAPIQuery('/configs/tenant-configurations/');

  const mutation = useAPIMutation(
    async (values: VixtingAdmissionSettingsFormValues, api) => {
      return await Promise.all([
        api.patch('/configs/tenant-configurations/vixting-days-to-admission/', {
          value: values.vixtingDaysToAdmission,
        }),
        api.patch('/configs/tenant-configurations/vixting-admission-date-reference/', {
          value: values.vixtingAdmissionDateReference,
        }),
      ]);
    },
    {
      onSuccess: () => {
        message.success(t('Configurações atualizadas.'));
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('/configs/tenant-configurations/');
      },
    }
  );

  return (
    <ErrorResult error={config.error}>
      <Helmet>
        <title>{t('Vixting data limite de admissão')}</title>
      </Helmet>
      <Spin spinning={config.isLoading}>
        <Form<VixtingAdmissionSettingsFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            vixtingDaysToAdmission: config.data?.vixtingDaysToAdmission,
            vixtingAdmissionDateReference: config.data?.vixtingAdmissionDateReference,
          }}
          onFinish={(values) => {
            mutation.mutate(values);
          }}
        >
          <Form.Legend>
            <Trans>Data limite de admissão</Trans>
          </Form.Legend>
          <Typography.Paragraph>
            <Trans>Escolha o critério para calcular a data limite na etapa de contratação.</Trans>
          </Typography.Paragraph>
          <Form.Col span={12}>
            <Form.Item
              name="vixtingDaysToAdmission"
              label={t('Quantidade de dias')}
              rules={[{ type: 'number' }]}
            >
              <InputNumber min={1} max={30} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="vixtingAdmissionDateReference"
              label={
                <Typography.Text>
                  <Trans>Referência da data limite de admissão</Trans>{' '}
                  <Tooltip
                    overlayStyle={{ maxWidth: '400px' }}
                    title={
                      <div style={{ textAlign: 'center' }}>
                        <strong>
                          <Trans>Pela data de admissão:</Trans>{' '}
                        </strong>
                        <Trans>
                          a data limite será calculada subtraindo a data de admissão do candidato
                          pela quantidade de dias definida.
                        </Trans>
                        <br />
                        <strong>
                          <Trans>Pela data atual:</Trans>{' '}
                        </strong>
                        <Trans>
                          a data limite será calculada somando a quantidade de dias definida à data
                          de hoje.
                        </Trans>
                      </div>
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </Typography.Text>
              }
              rules={[{ type: 'string' }]}
            >
              <Select>
                <Select.Option value="admission_date">
                  <Trans>Data de admissão</Trans>
                </Select.Option>
                <Select.Option value="current_date">
                  <Trans>Data atual</Trans>
                </Select.Option>
              </Select>
            </Form.Item>
          </Form.Col>
          <Form.Footer>
            <Form.FooterEnd>
              <Button type="primary" loading={mutation.isLoading} htmlType="submit">
                <Trans>Salvar alterações</Trans>
              </Button>
            </Form.FooterEnd>
          </Form.Footer>
        </Form>
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(VixtingAdmissionSettings);
